/**
 * SOW public-mount rate-limit — the FEP perimeter, Layer 1 (ADR-080 §4.3).
 *
 * A LINEAR escalating cooldown on the public sign-up submit: the first
 * RL_FREE_ATTEMPTS submits are free, then a required wait of 5 → 10 → 15 → 20 →
 * 25 → 30 seconds (capped at 30s) before each subsequent attempt. Linear (not
 * Fibonacci) — legible, tunable, predictable ceiling, a poorer target to
 * pen-test (ADR-080 §4.3).
 *
 * KEYED TO TIMING, NOT IDENTITY (§4.3): the state is just an attempt count + the
 * last-attempt timestamp — no IP, no email, no device (all spoofable). It is
 * PERSISTED (sessionStorage) so a reload cannot reset the cooldown — that
 * persistence is the real Lambda-cost protection: a caller cannot escape the
 * countdown by hammering reload/submit (§4.3, "the countdown modal blocks any
 * further reload/submit attempts during the delay").
 *
 * This is a CLIENT-SIDE UX + naive-script deterrent, NOT the whole wall (§4.3):
 * the real anti-pentest controls are the Cloudflare edge (Layer 0) and the
 * agent-armed circuit-breaker (Layer 2, server-side). A direct-to-API attacker
 * who bypasses this is caught there, not here.
 *
 * Pure module (no React, no DOM beyond the storage helpers) so the escalation and
 * the reload-survival can be unit-tested in isolation.
 */

/** Base cooldown after the first attempt (§4.3 — "once every 5 seconds"). */
export const RL_STEP_MS = 5_000;
/**
 * FREE ATTEMPTS — how many submits are served with NO cooldown at all.
 *
 * ⚠ THE LADDER USED TO START ON THE FIRST LEGITIMATE SUBMIT. `cooldownMs(1)`
 * returned 5000, and `recordAttempt` stamps the count on the way to the network,
 * so the very first honest sign-up submit armed a 5-second countdown modal. The
 * escalation was aimed at "how fast are you retrying" but it charged the opening
 * attempt, which is by definition not a retry — every real applicant met the
 * abuse box before they had done anything remotely abusive.
 *
 * ⭐ THIS IS A THRESHOLD, NOT A WEAKENING. The ladder is unchanged in shape and
 * ceiling; it is SHIFTED so attempt N+1 pays what attempt N used to. With the
 * value at 1 the second submit waits 5s, the third 10s, and the 30s cap still
 * arrives — a script hammering submit is deterred on exactly the same curve, one
 * request later. Idle decay (below) composes with this for free: a decayed state
 * reads as pristine, so a returning human gets their free attempt back.
 */
export const RL_FREE_ATTEMPTS = 1;
/** Ceiling — the cooldown never exceeds 30s (§4.3). */
export const RL_CAP_MS = 30_000;
/**
 * IDLE-DECAY WINDOW — go quiet for this long and the escalation resets to zero.
 *
 * ⚠ WITHOUT THIS THE COUNTER ONLY EVER CLIMBED. `attempts` was monotonic for the
 * life of the tab, so the cooldown was a function of "how many times have you ever
 * tried" rather than "how fast are you trying". A legitimate applicant who mistyped
 * their email, fixed it ten minutes later, then hit a validation error, was served
 * a 15s then 20s wait for behaviour that is not remotely abusive. The escalation
 * was correct; the absence of decay made it permanent.
 *
 * WHY FIVE MINUTES. It must sit comfortably above RL_CAP_MS (30s) or the decay
 * would erase the escalation faster than the escalation could build — at, say, a
 * 45s window, an attacker pacing attempts just over the cap would reset to free
 * every single time and Layer 1 would deter nothing. Five minutes is 10× the
 * ceiling, so the full 5→30s ladder still builds and holds against rapid retries,
 * while a human who wanders off and comes back reads as a fresh visitor.
 *
 * The residual: someone CAN hold `attempts` at 1 forever by pacing attempts 5
 * minutes apart. That is 12 requests/hour — not an abuse rate, and deliberately
 * not this layer's problem. Layer 1 is a client-side UX + naive-script deterrent
 * (see the header); a paced or direct-to-API attacker is caught by the Cloudflare
 * edge (Layer 0) and the server-side circuit-breaker (Layer 2), which are keyed to
 * things a client cannot reset by waiting.
 */
export const RL_IDLE_RESET_MS = 5 * 60_000;
/** sessionStorage key. Timing state only — no identity is ever stored here. */
export const RL_STORAGE_KEY = 'nexa.sow.rl';

export interface SowRateLimitState {
  /** Number of submit attempts recorded so far (drives the linear escalation). */
  attempts: number;
  /** Epoch-ms of the most recent recorded attempt; 0 when none yet. */
  lastAttemptAt: number;
}

export const INITIAL_RATE_LIMIT_STATE: SowRateLimitState = { attempts: 0, lastAttemptAt: 0 };

/**
 * The cooldown required AFTER `attempts` attempts, before the next is allowed:
 * the first RL_FREE_ATTEMPTS attempts are free, then linear 5→10→15→20→25→30s,
 * capped at 30s.
 *
 * ⭐ EVERY consumer routes through here — `nextAllowedAt`, `remainingMs`,
 * `remainingSeconds` and `canAttempt` all derive from this one function, which is
 * why the free-attempt threshold needed no other call-site change and cannot
 * drift out of step with the countdown the user is shown.
 */
export function cooldownMs(attempts: number): number {
  if (attempts <= RL_FREE_ATTEMPTS) return 0;
  return Math.min((attempts - RL_FREE_ATTEMPTS) * RL_STEP_MS, RL_CAP_MS);
}

/**
 * Apply the idle decay: a state untouched for RL_IDLE_RESET_MS reads as pristine.
 *
 * ⭐ EVERY consumer routes through this — `remainingMs`, `canAttempt` and
 * `recordAttempt` alike. That is not tidiness, it is the correctness argument: the
 * countdown the user SEES and the gate that BLOCKS them must be computed from the
 * same decayed state, or the box says "wait 12s" while the submit is already
 * allowed (or worse, the reverse). Applying decay in only some of the three would
 * manufacture exactly the display/behaviour divergence this build was asked to
 * rule out. `decay-is-total.test.ts` pins the invariant
 * `canAttempt(s, t) === (remainingSeconds(s, t) === 0)` over a matrix rather than
 * trusting the three call sites to stay in step.
 *
 * Pure — returns the input state when no decay applies, so callers can pass the
 * result straight to `saveRateLimitState` without a needless write.
 */
export function decay(state: SowRateLimitState, now: number): SowRateLimitState {
  if (state.attempts <= 0) return state;
  // A future/garbage `lastAttemptAt` (clock skew, a hand-edited storage value)
  // yields a negative idle span and must NOT decay — treat it as "just now".
  const idleMs = now - state.lastAttemptAt;
  if (idleMs >= RL_IDLE_RESET_MS) return { ...INITIAL_RATE_LIMIT_STATE };
  return state;
}

/** The epoch-ms at which the next attempt becomes allowed. */
export function nextAllowedAt(state: SowRateLimitState): number {
  return state.lastAttemptAt + cooldownMs(state.attempts);
}

/** Milliseconds still to wait before the next attempt (0 when allowed now). */
export function remainingMs(state: SowRateLimitState, now: number): number {
  const s = decay(state, now);
  if (s.attempts <= 0) return 0;
  return Math.max(0, nextAllowedAt(s) - now);
}

/** Whole seconds still to wait, rounded UP (for the countdown display). */
export function remainingSeconds(state: SowRateLimitState, now: number): number {
  return Math.ceil(remainingMs(state, now) / 1000);
}

/** May an attempt proceed now? The first RL_FREE_ATTEMPTS attempts are always
 *  allowed; thereafter only once the linear cooldown has elapsed. */
export function canAttempt(state: SowRateLimitState, now: number): boolean {
  return remainingMs(state, now) === 0;
}

/** Record an attempt — increments the count and stamps the time. Pure; returns
 *  the next state. Call this whenever a submit reaches the network (success OR
 *  failure), because the cost being throttled is the Lambda invocation. */
export function recordAttempt(state: SowRateLimitState, now: number): SowRateLimitState {
  // Decay FIRST, then count. An attempt arriving after a long idle gap is the
  // FIRST attempt of a new burst and must be recorded as such — incrementing the
  // stale count here would re-introduce the monotonic climb through the back door,
  // leaving the user free to submit once and then immediately throttled at the old
  // escalated rate.
  const s = decay(state, now);
  return { attempts: s.attempts + 1, lastAttemptAt: now };
}

// ---------------------------------------------------------------------------
// Persistence (sessionStorage) — reload-survival. Defensive: any parse/quota
// failure degrades to the in-memory default rather than throwing into the UI.
// ---------------------------------------------------------------------------

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStore(): StorageLike | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null; // sessionStorage can throw in privacy modes
  }
}

export function loadRateLimitState(store: StorageLike | null = defaultStore()): SowRateLimitState {
  if (!store) return { ...INITIAL_RATE_LIMIT_STATE };
  try {
    const raw = store.getItem(RL_STORAGE_KEY);
    if (!raw) return { ...INITIAL_RATE_LIMIT_STATE };
    const parsed = JSON.parse(raw) as Partial<SowRateLimitState>;
    const attempts = Number.isFinite(parsed.attempts) ? Math.max(0, Math.floor(parsed.attempts as number)) : 0;
    const lastAttemptAt = Number.isFinite(parsed.lastAttemptAt) ? Math.max(0, parsed.lastAttemptAt as number) : 0;
    return { attempts, lastAttemptAt };
  } catch {
    return { ...INITIAL_RATE_LIMIT_STATE };
  }
}

export function saveRateLimitState(state: SowRateLimitState, store: StorageLike | null = defaultStore()): void {
  if (!store) return;
  try {
    store.setItem(RL_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / privacy mode — the in-memory state still enforces this session */
  }
}
